import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import { useOrganization } from '../contexts/OrganizationContext';
import { useProfile } from '../contexts/ProfileContext';
import { Permission } from '../types';
import { permissionService } from '../services/permissionService';
import { cacheService } from '../services/cacheService';

export const usePermissions = () => {
  const { user } = useAuth();
  const { currentOrganization, loading: orgLoading } = useOrganization();
  const { profile } = useProfile();
  const [userPermissions, setUserPermissions] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const loadingRef = useRef(false);

  // Carregar permissões
  const loadPermissions = useCallback(async () => {
    // Se a organização ainda está carregando, manter loading = true
    if (orgLoading) {
      setLoading(true);
      return;
    }

    if (!user?.uid || !currentOrganization?.id) {
      setLoading(false);
      return;
    }

    // Evitar múltiplas chamadas simultâneas
    if (loadingRef.current) {
      return;
    }

    try {
      loadingRef.current = true;
      
      // Verificar cache primeiro
      const cachedPermissions = cacheService.getCachedPermissions(currentOrganization.id, user.uid);
      if (cachedPermissions) {
        setUserPermissions(cachedPermissions);
        setLoading(false);
        loadingRef.current = false;
        return;
      }

      // Se não tem cache, buscar da API
      setLoading(true);
      const permissions = await permissionService.getMyPermissions(currentOrganization.id);
      
      // Salvar no cache
      cacheService.setCachedPermissions(currentOrganization.id, user.uid, permissions);
      
      setUserPermissions(permissions);
    } catch (error) {
      console.error('Error loading permissions:', error);
      setUserPermissions({});
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [user?.uid, currentOrganization?.id, orgLoading]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  const hasPermission = useCallback((permission: Permission): boolean => {
    if (!currentOrganization || !user) {
      return false;
    }

    // Respeitar as permissões retornadas pelo backend (que já considera templates customizados)
    // Não fazer bypass automático para owner - o backend deve retornar as permissões corretas
    return userPermissions[permission] === true;
  }, [userPermissions, currentOrganization, user]);

  const isOwner = useMemo(() => {
    return currentOrganization?.ownerId === user?.uid;
  }, [currentOrganization, user]);

  const isAdmin = useMemo(() => {
    const userRole = profile?.role || (profile as any)?.Role;
    return isOwner || userRole === 'admin';
  }, [isOwner, profile]);

  const reloadPermissions = useCallback(async () => {
    if (!user?.uid || !currentOrganization?.id) return;
    
    // Limpar cache e recarregar
    cacheService.clearPermissionsCache(currentOrganization.id, user.uid);
    await loadPermissions();
  }, [user?.uid, currentOrganization?.id, loadPermissions]);

  return {
    hasPermission,
    userPermissions,
    loading,
    isOwner,
    isAdmin,
    reloadPermissions,
  };
};
